import { InlineUser } from "@/components";
import { InlineActiveDirectory } from "@/components/InlineActiveDirectory";
import { HistoryLogAction } from "@/enums";
import { IHistoryLogTableModel } from "@/interfaces";
import { HistoryLogPropertyDiff } from "@/types";
import { Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { Fragment, useCallback, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import HistoryLogDiffCell from "./HistoryLogDiffCell";

interface IProps {
  historyLog: IHistoryLogTableModel;
  pagination: false;
}

interface ICellProps {
  value?: boolean | string | number | null;
  isAdGroup?: boolean;
  isUserId?: boolean;
}

export const HistoryLogDiffTable = ({ historyLog, ...props }: IProps) => {
  const displayCell = useCallback(
    ({ value, isAdGroup, isUserId }: ICellProps) => {
      if (isAdGroup && typeof value === "string") {
        const adGroups = value.split(", ");
        return adGroups.map((adGroupId, index) => (
          <Fragment key={adGroupId}>
            {index > 0 && ", "}
            <InlineActiveDirectory activeDirectoryId={adGroupId} />
          </Fragment>
        ));
      }

      if (isUserId && typeof value === "string") {
        return <InlineUser userId={value} />;
      }
      return <HistoryLogDiffCell value={value} />;
    },
    []
  );

  const columns: ColumnsType<HistoryLogPropertyDiff> = useMemo(() => {
    const cols: ColumnsType<HistoryLogPropertyDiff> = [
      {
        key: "property",
        dataIndex: "property",
        title: <FormattedMessage id="historyLog.propertyCol" />,
        width: 250,
      },
    ];

    if (historyLog.action !== HistoryLogAction.Create) {
      cols.push({
        key: "oldValue",
        title: <FormattedMessage id="historyLog.oldValueCol" />,
        width: 300,
        render: (_, record) =>
          displayCell({
            value: record.oldValue,
            isAdGroup: record.isAdGroup,
            isUserId: record.isUserId,
          }),
      });
    }

    cols.push({
      key: "newValue",
      title: <FormattedMessage id="historyLog.newValueCol" />,
      render: (_, record) =>
        displayCell({
          value: record.newValue,
          isAdGroup: record.isAdGroup,
          isUserId: record.isUserId,
        }),
    });

    return cols;
  }, [historyLog, displayCell]);

  return (
    <Table
      {...props}
      dataSource={historyLog.differences}
      rowKey={(changelog) => changelog.id}
      columns={columns}
    />
  );
};
